import React, { useState } from 'react';
import { ExportModal } from './ExportModal';

interface ExportButtonProps {
  data: any[];
  columns: Array<{
    header: string;
    key: string;
    dataKey?: string;
  }>;
  title: string;
  teamName?: string;
  seasonName?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'outline';
  sortConfig?: {
    key: string;
    direction: 'asc' | 'desc';
  } | null;
  clientId: string | null;
}

export const ExportButton: React.FC<ExportButtonProps> = ({
  data,
  columns,
  title,
  teamName,
  seasonName,
  className = '',
  size = 'md',
  variant = 'primary',
  sortConfig,
  clientId,
}) => {
  const [showExportModal, setShowExportModal] = useState(false);

  // Filter out non-exportable columns and handle special columns
  const calculatedSpecialColumns = ['PPG', 'GPG', 'GAPG'];

  const exportableColumns = columns
    .filter(
      (col) =>
        !col.header.toLowerCase().includes('position') &&
        !col.header.toLowerCase().includes('#') &&
        !col.header.toLowerCase().includes('player name') &&
        !col.header.toLowerCase().includes('player') &&
        col.header !== 'GP' && // Filter out GP since we add "Games Played" separately
        col.header !== 'G+/-' && // Filter out Goals +/- (special column)
        // For special columns, only include those in our calculated list
        (col.key !== 'special' || calculatedSpecialColumns.includes(col.header)),
    )
    .map((col) => {
      // Handle special columns (PPG, GPG, GAPG)
      if (col.key === 'special') {
        return {
          header: col.header,
          key: col.header,
          dataKey: col.header,
        };
      }
      return {
        ...col,
        dataKey: col.key,
      };
    });

  // Calculate special fields based on what columns exist
  const hasPPG = columns.some((col) => col.key === 'special' && col.header === 'PPG');
  const hasGPG = columns.some((col) => col.key === 'special' && col.header === 'GPG');
  const hasGAPG = columns.some((col) => col.key === 'special' && col.header === 'GAPG');

  const exportData =
    hasPPG || hasGPG || hasGAPG
      ? data.map((player: any) => {
          const enhanced: any = { ...player };

          if (hasPPG && player.points !== undefined && player.gamesPlayed !== undefined) {
            enhanced.PPG = player.gamesPlayed > 0 ? parseFloat((player.points / player.gamesPlayed).toFixed(1)) : 0;
          }

          if (hasGPG && player.goals !== undefined && player.gamesPlayed !== undefined) {
            enhanced.GPG = player.gamesPlayed > 0 ? parseFloat((player.goals / player.gamesPlayed).toFixed(1)) : 0;
          }

          if (hasGAPG && player.goalsAllowed !== undefined && player.gamesPlayed !== undefined) {
            enhanced.GAPG =
              player.gamesPlayed > 0 ? parseFloat((player.goalsAllowed / player.gamesPlayed).toFixed(1)) : 0;
          }

          return enhanced;
        })
      : data;

  // Always add player identification columns for export since we filtered out the "Player" column
  const exportColumns = [
    { header: '#', key: 'playerNumber', dataKey: 'playerNumber' },
    { header: 'Player Name', key: 'playerName', dataKey: 'playerName' },
    { header: 'Games Played', key: 'gamesPlayed', dataKey: 'gamesPlayed' },
    ...exportableColumns,
  ];

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-2 text-base',
  };

  const variantClasses = {
    primary: 'bg-green-600 text-white hover:bg-green-700 border-green-600',
    secondary: 'bg-gray-600 text-white hover:bg-gray-700 border-gray-600',
    outline: 'bg-white text-green-600 border-green-600 hover:bg-green-50',
  };

  const iconSize = size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';

  return (
    <>
      <button
        onClick={() => setShowExportModal(true)}
        className={`
          inline-flex items-center gap-2 border rounded-md font-medium transition-colors duration-200
          focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          ${sizeClasses[size]}
          ${variantClasses[variant]}
          ${className}
        `}
        title={`Export ${title} as PDF`}
      >
        <svg className={iconSize} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        Export
      </button>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={exportData}
        columns={exportColumns}
        title={title}
        teamName={teamName}
        seasonName={seasonName}
        sortConfig={sortConfig}
        clientId={clientId}
      />
    </>
  );
};
